import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Calculate the bounding box and center point from multiple coordinates
 */
export function calculateMapBounds(coordinates: { lat: number; long: number }[]) {
  if (coordinates.length === 0) {
    return {
      centerLatitude: 0,
      centerLongitude: 0,
      minLat: 0,
      maxLat: 0,
      minLon: 0,
      maxLon: 0
    };
  }

  if (coordinates.length === 1) {
    const coord = coordinates[0];
    return {
      centerLatitude: coord.lat,
      centerLongitude: coord.long,
      minLat: coord.lat,
      maxLat: coord.lat,
      minLon: coord.long,
      maxLon: coord.long
    };
  }

  const lats = coordinates.map(coord => coord.lat);
  const lons = coordinates.map(coord => coord.long);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const centerLatitude = (minLat + maxLat) / 2;
  const centerLongitude = (minLon + maxLon) / 2;

  return {
    centerLatitude,
    centerLongitude,
    minLat,
    maxLat,
    minLon,
    maxLon
  };
}

/**
 * Calculate zoom level for a specific radius in kilometers around a point
 * This calculates the zoom level needed to show a circular area of the specified radius
 */
export function calculateZoomForRadius(
  centerLat: number,
  centerLon: number,
  radiusKm: number,
  minZoom: number = 5,
  maxZoom: number = 16
): number {
  // Convert radius from km to degrees (approximate)
  // 1 degree of latitude ≈ 111 km
  // 1 degree of longitude ≈ 111 km * cos(latitude)
  const latRadiusDegrees = radiusKm / 111;
  
  // Handle edge cases for very high latitudes (near poles)
  const cosLat = Math.cos(Math.abs(centerLat) * Math.PI / 180);
  const lonRadiusDegrees = cosLat > 0.01 ? radiusKm / (111 * cosLat) : latRadiusDegrees;
  
  // For very high latitudes (> 75°), use a zoom level that actually shows 300km
  // as the calculation becomes unreliable near the poles
  if (Math.abs(centerLat) > 75) {
    return 3.5; // Zoom level 3.5 shows approximately 300km at high latitudes
  }
  
  // Use the smaller radius to ensure the 300km circle fits within the viewport
  // This prevents showing too much area beyond the intended 300km radius
  const maxRadiusDegrees = Math.min(latRadiusDegrees, lonRadiusDegrees);
  
  // Calculate zoom level based on the radius
  // This is an approximation - Mapbox zoom levels roughly double the scale each level
  // Starting from zoom level 0 (world view) to zoom level 22 (building level)
  let zoomLevel = Math.log2(360 / (maxRadiusDegrees * 2));
  
  // Fine-tune the zoom level based on empirical testing
  // The calculated zoom tends to show much more area than intended
  // So we increase the zoom level significantly to compensate
  zoomLevel += 5.0;
  
  // Clamp to min/max zoom levels and return the exact zoom level (not floored)
  // This gives us more precise control over the zoom level
  return Math.max(minZoom, Math.min(maxZoom, zoomLevel));
}

/**
 * Calculate the actual radius in km that would be shown at a given zoom level
 * This is the inverse of calculateZoomForRadius for verification
 */
export function calculateRadiusForZoom(
  centerLat: number,
  centerLon: number,
  zoomLevel: number
): number {
  // Convert zoom level back to degrees
  const degreesAtZoom = 360 / Math.pow(2, zoomLevel);
  
  // Convert degrees to km
  const latRadiusKm = (degreesAtZoom / 2) * 111;
  const lonRadiusKm = (degreesAtZoom / 2) * 111 * Math.cos(Math.abs(centerLat) * Math.PI / 180);
  
  // Return the smaller radius (what actually fits in the viewport)
  return Math.min(latRadiusKm, lonRadiusKm);
}

/**
 * Calculate appropriate zoom level to show all coordinates within the map view
 * This is a simplified calculation - Mapbox has more sophisticated bounds fitting
 */
export function calculateZoomLevel(
  coordinates: { lat: number; long: number }[],
  minZoom: number = 10,
  maxZoom: number = 16
): number {
  if (coordinates.length <= 1) {
    return maxZoom;
  }

  const bounds = calculateMapBounds(coordinates);
  const latDiff = bounds.maxLat - bounds.minLat;
  const lonDiff = bounds.maxLon - bounds.minLon;
  const maxDiff = Math.max(latDiff, lonDiff);

  // Simple zoom calculation based on the maximum difference
  // Smaller differences = higher zoom levels
  if (maxDiff <= 0.001) return maxZoom; // Very close properties
  if (maxDiff <= 0.01) return 15; // Close properties
  if (maxDiff <= 0.05) return 14; // Medium distance
  if (maxDiff <= 0.1) return 13; // Medium-far distance
  if (maxDiff <= 0.2) return 12; // Far distance
  if (maxDiff <= 0.5) return 11; // Very far distance
  if (maxDiff <= 1) return 10; // Extremely far distance

  return minZoom;
}
